import { useState } from 'react';
import type { EPCR, MedicationEntry, AllergyEntry } from '@/types';
import { cn } from '@/lib/cn';
import { X, Plus } from 'lucide-react';
import { SearchableSelect } from '@/components/SearchableSelect';
import { MedicationAlerts } from '@/components/MedicationAlerts';
import {
  MEDICAL_HISTORY, MEDICATIONS,
  DRUG_ALLERGIES, ENVIRONMENTAL_ALLERGIES,
  flagsForMedication,
} from '@/lib/pt-history-dictionaries';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoPatientFields } from '@/lib/demo-data';

// Build a set of known drug-allergy labels so the single allergies[]
// array can be split at render time into drug vs environmental without
// storing the category on disk (category is derivable). A label not in
// this set is treated as environmental. NKA / NKDA are tracked
// independently via the blanket toggles below.
const DRUG_ALLERGY_LABELS = new Set<string>(
  DRUG_ALLERGIES.flatMap((g) => g.options.map((o) => o.label)),
);

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

/**
 * MedicalHistoryTab — patient history, medications, and allergies.
 *
 * Moved out of PatientDataTab so demographics and clinical history
 * render on independent tabs. In the Command-view (right-rail) this
 * means the ID/demographics aren't visually stacked with the
 * bulkier medical-history dropdowns.
 *
 * Sections:
 *   1. Medical History   — conditions, prior surgeries, devices
 *   2. Current Medications — searchable + editable rows; clinical
 *                            flags (blood thinner / insulin / opioid /
 *                            etc.) surface via MedicationAlerts.
 *   3. Allergies         — NKA + NKDA toggles (both can be set;
 *                          coexist with specific allergens); searchable
 *                          dropdown grouped by Antibiotics / NSAIDs /
 *                          Opioids / Local Anesthetics / Contrast /
 *                          Foods / Environmental-Stings-Bites / etc.
 */
export function MedicalHistoryTab({ epcr, onUpdate }: Props) {
  const hasNKA = epcr.allergies.some((a) => a.agent === 'NKA');
  const hasNKDA = epcr.allergies.some((a) => a.agent === 'NKDA');
  // Specific allergies (exclude the blanket flags), further split into
  // drug vs environmental by checking against the drug-allergy label
  // set. Anything not recognized as a drug is treated as
  // environmental / other.
  const specificAllergies = epcr.allergies.filter(
    (a) => a.agent !== 'NKA' && a.agent !== 'NKDA',
  );
  const drugAllergies = specificAllergies.filter((a) => DRUG_ALLERGY_LABELS.has(a.agent));
  const envAllergies = specificAllergies.filter((a) => !DRUG_ALLERGY_LABELS.has(a.agent));

  function toggleNKA(kind: 'NKA' | 'NKDA') {
    const currently = kind === 'NKA' ? hasNKA : hasNKDA;
    const without = epcr.allergies.filter((a) => a.agent !== kind);
    if (currently) {
      onUpdate({ allergies: without });
    } else {
      onUpdate({
        allergies: [
          ...without,
          { agent: kind, drug_class: null, reaction_type: '' },
        ],
      });
    }
  }

  function setAllergiesForCategory(
    categoryList: AllergyEntry[],
    nextAgents: string[],
  ) {
    // Rebuild the full allergies array preserving the OTHER category
    // and the blanket NKA/NKDA flags, and swapping in the new selection
    // for the category being edited.
    const preservedAgents = new Set(categoryList.map((a) => a.agent));
    const remaining = epcr.allergies.filter(
      (a) => !preservedAgents.has(a.agent),
    );
    const rebuilt: AllergyEntry[] = nextAgents.map((ag) => {
      const existing = categoryList.find((a) => a.agent === ag);
      return existing ?? { agent: ag, drug_class: null, reaction_type: '' };
    });
    onUpdate({ allergies: [...remaining, ...rebuilt] });
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold uppercase tracking-tight text-slate-100">
            Medical History, Medications & Allergies
          </h2>
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mt-0.5">
            Independent of Patient Demographics · NEMSIS ePatient 10–13
          </p>
        </div>
        <DemoFillButton onFill={() => onUpdate(demoPatientFields())} />
      </div>

      {/* ============================================================
          1. Medical History
          ============================================================ */}
      <section className="border border-slate-700 bg-slate-900/50">
        <header className="border-b border-slate-700 bg-slate-900 px-3 py-1.5">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
            Medical History
          </h3>
        </header>
        <div className="p-3 space-y-3">
          <SearchableSelect
            mode="multi"
            groups={MEDICAL_HISTORY}
            value={epcr.medical_history}
            onChange={(next) => onUpdate({ medical_history: next })}
            placeholder="Search conditions, devices, surgical history…"
            disabled={epcr.locked}
            allowFreeText
          />
          {/* Manual entry for anything outside the curated list — rare
              conditions, surgical specifics, device variants. Adds the
              typed text as a chip in the same medical_history array,
              so downstream consumers (NEMSIS, narrative, etc.) treat
              it identically to a list-picked entry. */}
          <ManualHistoryEntry
            disabled={epcr.locked}
            onAdd={(text) => {
              const trimmed = text.trim();
              if (!trimmed) return;
              if (epcr.medical_history.includes(trimmed)) return;
              onUpdate({ medical_history: [...epcr.medical_history, trimmed] });
            }}
          />
        </div>
      </section>

      {/* ============================================================
          2. Current Medications — name-only rows. Dose / frequency
          were removed as noise; detailed dosing rarely affects field
          care and adds bulk. Clinical flags (blood thinner / insulin /
          opioid) still surface via MedicationAlerts so the crew sees
          the risk signal without needing a dose column.
          ============================================================ */}
      <section className="border border-slate-700 bg-slate-900/50">
        <header className="border-b border-slate-700 bg-slate-900 px-3 py-1.5">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
            Current Medications
          </h3>
        </header>
        <div className="p-3 space-y-2">
          <MedicationAlerts medications={epcr.current_medications} />
          <SearchableSelect
            mode="multi"
            groups={MEDICATIONS}
            value={epcr.current_medications.map((m) => m.name)}
            onChange={(names) => {
              const byName = new Map(epcr.current_medications.map((m) => [m.name, m]));
              const next: MedicationEntry[] = names.map((n) => {
                const prior = byName.get(n);
                if (prior) return prior;
                const flags = flagsForMedication(n);
                return {
                  name: n,
                  dosage: '',
                  generic_name: null,
                  clinical_flags: flags.length > 0 ? flags : null,
                };
              });
              onUpdate({ current_medications: next });
            }}
            placeholder="Search medications, or type a new one…"
            disabled={epcr.locked}
            allowFreeText
          />
        </div>
      </section>

      {/* ============================================================
          3. Allergies — NKA + NKDA can BOTH be set and coexist with
          specific allergies (addresses the prior mutual-exclusion bug).
          ============================================================ */}
      <section className="border border-slate-700 bg-slate-900/50">
        <header className="border-b border-slate-700 bg-slate-900 px-3 py-1.5">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
            Allergies
          </h3>
        </header>
        <div className="p-3 space-y-2">
          {/* Blanket toggles — both can be active simultaneously + can
              sit alongside specific allergen entries. NKA = "no known
              allergies" covers environmental / food / material; NKDA =
              "no known drug allergies" specifically. A patient can have
              both flags (never documented any allergies of any kind)
              or one or the other. They can ALSO have a specific
              allergen AND one of these flags marked for partial
              confirmation (rare — mostly for partial patient report). */}
          <div className="flex items-center gap-2 flex-wrap">
            <BlanketToggle
              label="NKA"
              hint="No Known Allergies"
              on={hasNKA}
              onToggle={() => toggleNKA('NKA')}
              disabled={epcr.locked}
            />
            <BlanketToggle
              label="NKDA"
              hint="No Known Drug Allergies"
              on={hasNKDA}
              onToggle={() => toggleNKA('NKDA')}
              disabled={epcr.locked}
            />
            <span className="text-[10px] text-slate-500 ml-1 italic">
              Both can be selected together. Specific allergens below.
            </span>
          </div>

          {/* Drug allergies — medications + medication classes +
              contrast + vaccines. Kept independent of environmental so
              the two clinical concepts don't bleed into each other on
              screen. No reaction field (dropped as noise). */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Drug Allergies
            </div>
            <SearchableSelect
              mode="multi"
              groups={DRUG_ALLERGIES}
              value={drugAllergies.map((a) => a.agent)}
              onChange={(agents) => setAllergiesForCategory(drugAllergies, agents)}
              placeholder="Search antibiotics, NSAIDs, opioids, anesthetics, contrast, vaccines…"
              disabled={epcr.locked}
              allowFreeText
            />
            {drugAllergies.length > 0 && (
              <AllergyChipList
                entries={drugAllergies}
                locked={epcr.locked}
                onRemove={(agent) =>
                  onUpdate({
                    allergies: epcr.allergies.filter((x) => x.agent !== agent),
                  })
                }
              />
            )}
          </div>

          {/* Environmental / other allergies — foods, stings/bites,
              airborne, animal dander, adhesives, chemical. */}
          <div className="space-y-1 pt-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Environmental & Other Allergies
            </div>
            <SearchableSelect
              mode="multi"
              groups={ENVIRONMENTAL_ALLERGIES}
              value={envAllergies.map((a) => a.agent)}
              onChange={(agents) => setAllergiesForCategory(envAllergies, agents)}
              placeholder="Search foods, stings, airborne, dander, adhesives, chemicals…"
              disabled={epcr.locked}
              allowFreeText
            />
            {envAllergies.length > 0 && (
              <AllergyChipList
                entries={envAllergies}
                locked={epcr.locked}
                onRemove={(agent) =>
                  onUpdate({
                    allergies: epcr.allergies.filter((x) => x.agent !== agent),
                  })
                }
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function BlanketToggle({
  label, hint, on, onToggle, disabled,
}: {
  label: string;
  hint: string;
  on: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'flex items-baseline gap-1.5 border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors',
        on
          ? 'bg-emerald-600 text-white border-emerald-400'
          : 'bg-slate-800 text-slate-400 border-slate-600 hover:bg-slate-700 hover:text-slate-200',
      )}
      aria-pressed={on}
      title={hint}
    >
      <span>{label}</span>
      <span className={cn('text-[10px] font-normal normal-case opacity-80', on && 'opacity-100')}>
        · {hint}
      </span>
    </button>
  );
}

// ------------------------------------------------------------------
// AllergyChipList — compact read-only chip row with a tiny remove (x)
// button. No reaction / dosage inputs (dropped as noise per addendum
// + Chief feedback — the narrative captures reaction details when it
// matters).
// ------------------------------------------------------------------
function AllergyChipList({
  entries, locked, onRemove,
}: {
  entries: AllergyEntry[];
  locked: boolean;
  onRemove: (agent: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 pt-0.5">
      {entries.map((a) => (
        <span
          key={a.agent}
          className={cn(
            'inline-flex items-center gap-1 border border-amber-700 bg-amber-950/40 px-2 py-0.5 text-xs text-amber-200',
          )}
        >
          {a.agent}
          {!locked && (
            <button
              type="button"
              onClick={() => onRemove(a.agent)}
              className="ml-0.5 text-amber-400 hover:text-amber-100"
              title={`Remove ${a.agent}`}
            >
              <X size={11} />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * ManualHistoryEntry — free-text input for medical history items
 * outside the curated condition list. Adds typed text as a new chip
 * in the medical_history array on Enter or button-tap. Trims, dedupes
 * against the existing list, and clears itself for the next entry.
 *
 * Use case: rare conditions ("post-radiation neurogenic bladder",
 * "L renal artery stenosis s/p stent 2019") that aren't in the
 * 200-condition picker but matter clinically.
 */
function ManualHistoryEntry({
  disabled, onAdd,
}: {
  disabled: boolean;
  onAdd: (text: string) => void;
}) {
  const [text, setText] = useState('');
  function submit() {
    const t = text.trim();
    if (!t) return;
    onAdd(t);
    setText('');
  }
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
        Add condition not in the list
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          disabled={disabled}
          className="input-field flex-1"
          placeholder="e.g. post-radiation cystitis, R renal artery stenosis 2019…"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !text.trim()}
          className="rail-button rail-action shrink-0 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>
    </div>
  );
}
